import {
  Article,
  ArticleAction,
  ArticleImage,
  ArticleList,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { getOrderCurrency } from "@babackapp/return-widget-components/lib/financials.lib";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useLineItemLayout } from "../../hooks/use-line-item-layout";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { LineItemSubtitle } from "./line-item-subtitle";

type LineItemBarVariant = "floating" | "inline";

type LineItemBarProps = {
  /** `floating` pins the bar to the bottom of the widget. `inline` renders it in the page flow. */
  variant?: LineItemBarVariant;
  /** Slides the bar in from below. Leave off when a previous bar hands over through `layoutId`. */
  slideIn?: boolean;
};

// The page stacks its sections with `gap-10`: the negative margin brings the
// options back to 16px under the inline bar. `compact` already uses a 16px gap.
const BAR_CLASS_NAMES: Record<LineItemBarVariant, string> = {
  floating:
    "absolute bottom-4 left-4 w-auto right-4 z-10 drop-shadow-md overflow-hidden bg-background",
  inline: "-mb-6 compact:mb-0 overflow-hidden bg-background",
};

export function LineItemBar({
  variant = "floating",
  slideIn = false,
}: LineItemBarProps) {
  const {
    lineItem,
    quantity,
    setQuantity,
    returnableQty,
    removeItem,
    lineItemId,
  } = useLineItemLayout();
  const { orderId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);

  if (!lineItem) return null;

  return (
    <ArticleList
      layoutId="line-item-bar"
      // Position only: the two variants differ in width, and animating the
      // size would stretch the text while the bar travels.
      layout="position"
      className={BAR_CLASS_NAMES[variant]}
      variants={{}}
      initial={slideIn ? { y: 100 } : false}
      animate={{ y: 0, transition: { type: "spring" } }}
    >
      {/* Each step mounts its own bar: without this the row would replay its
          list enter and exit on every step. */}
      <Article disableVariants={{ enter: true, exit: true }}>
        <ArticleImage
          src={lineItem.image ?? undefined}
          layoutId={`img-${lineItemId}`}
        />
        <ArticleTitle>{lineItem.title}</ArticleTitle>
        <LineItemSubtitle
          lineItem={lineItem}
          currency={getOrderCurrency(order)}
        />
        <ArticleAction autoHide={false}>
          {returnableQty > 1 ? (
            <div className="flex items-center gap-1 border rounded-lg h-8 px-1 text-xs font-mono font-medium tabular-nums">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-1 text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Minus className="size-3" />
              </button>
              <span className="min-w-4 text-center">{quantity}</span>
              <button
                type="button"
                onClick={() =>
                  setQuantity(Math.min(returnableQty, quantity + 1))
                }
                className="px-1 text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Plus className="size-3" />
              </button>
            </div>
          ) : (
            <span className="text-xs font-mono font-medium tabular-nums text-muted-foreground">
              ×1
            </span>
          )}
          <Button
            size="icon"
            variant="outline"
            type="button"
            onClick={removeItem}
          >
            <Trash2 />
          </Button>
        </ArticleAction>
      </Article>
    </ArticleList>
  );
}
